import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuid } from 'uuid'

import { Modal, Typography, Box, IconButton, Snackbar, Alert, FormControlLabel, Radio, RadioGroup, Tooltip } from "@mui/material"
import ModalBox from "../themes/overrides/ModalBox"
import AddButton from "../themes/overrides/AddButton";
import CopyIcon from "../components/icons/CopyIcon"
import DeleteIcon from "../assets/images/icons/delete_icon.svg"

import { putWorkspace } from "../actions/workspaceAction";
import LoadingOverlay from "./LoadingOverlay";
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import PropTypes from 'prop-types';

const ManageAPIKeysModal = ({ open, onClose, selectedType, onSuccess }) => {
    const dispatch = useDispatch();
    const [generatedKey, setGeneratedKey] = useState([]);
    const [generatedKeyType, setGeneratedKeyType] = useState(null);
    const [apiKeys, setApiKeys] = useState({ read: [], write: [] });
    const [keyType, setKeyType] = useState('read');
    const [loading, setLoading] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const currentWorkspace = useSelector((state) => state.workspace.workspaceInfo);

    useEffect(() => {
        if (selectedType) {
            setApiKeys({
                read: currentWorkspace?.data?.apiKeys || [],
                write: currentWorkspace?.data?.writeApiKeys || []
            });
        }
    }, [currentWorkspace, selectedType]);

    const handleGenerateAPI = () => {
        const generatedUuid = uuid()
        const prefix = keyType === 'write' ? 'W_' : 'R_';
        const newKey = `CCMS_${prefix}${generatedUuid}`;
        setGeneratedKey([newKey]);
        setGeneratedKeyType(keyType);
    };

    const handleCopy = async (key) => {
        try {
            await navigator.clipboard.writeText(key);
            showSnackbar('Api Key copied to clipboard!', 'success');
        } catch (err) {
            showSnackbar('Failed to copy', 'error');
        }
    }

    const handleSaveAPI = async () => {
        if (generatedKey) {
            setLoading(true)
            try {
                const workspaceId = selectedType._id;
                const type = generatedKeyType === 'write' ? 'newWriteApiKeys' : 'newApiKeys';
                await dispatch(putWorkspace(workspaceId, generatedKey, type))
                    .then(() => {
                        setGeneratedKey([]);
                        setGeneratedKeyType(null);
                        onSuccess?.();
                    });
                showSnackbar('API Key saved successfully!', 'success');
            } catch (error) {
                showSnackbar('Failed to save API keys', 'error');
                setOpenSnackbar(true);
                console.error('Error saving API keys:', error);
            } finally {
                setLoading(false);
            }
        } else {
            showSnackbar('No API keys to save', 'warning');
        }
    }

    const handleDeleteAPI = async (apiKey) => {
        setLoading(true);
        try {
            let selectedApiKeys = [apiKey];
            const workspaceId = selectedType._id;
            const isWriteKey = apiKey.includes('W_');
            const type = isWriteKey ? 'removeWriteApiKeys' : 'removeApiKeys';
            await dispatch(putWorkspace(workspaceId, selectedApiKeys, type))
                .then(() => {
                    onSuccess?.();
                    selectedApiKeys = [];
                })
            showSnackbar('API Key removed successfully!', 'success');
        } catch (error) {
            showSnackbar('Failed to remove API keys', 'error');
            console.error('Error saving API keys:', error);
        } finally {
            setLoading(false);
        }
    }

    const showSnackbar = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    };

    const ApiKeyBox = ({ apiKey, onCopy, onDelete }) => (
        <Box
            key={apiKey}
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                mb: 2,
                border: '1px solid #ECECEC',
                borderRadius: 1,
                width: '100%',
                gap: 1
            }}
        >
            <Tooltip title={apiKey} placement="top-start">
                <Typography
                    sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '70%'
                    }}
                >
                    {apiKey}
                </Typography>
            </Tooltip>
            <Box sx={{ display: 'flex', flexShrink: 0 }}>
                <IconButton
                    color="secondary"
                    onClick={() => onCopy(apiKey)}
                    aria-label="copy"
                    size="small"
                    sx={{ mr: 1 }}
                >
                    <CopyIcon />
                </IconButton>
                <IconButton
                    size="small"
                    sx={{ color: '#FF4842' }}
                    onClick={() => onDelete(apiKey)}
                >
                    <img src={DeleteIcon} alt="delete" />
                </IconButton>
            </Box>
        </Box>
    );


    return (
        <Modal open={open} onClose={onClose}>
            <ModalBox sx={{ width: '700px', maxWidth: '95%' }}>
                <Typography variant="h2" sx={{ mb: 3 }}>Manage API Keys</Typography>
                <Box
                    sx={{
                        p: isMobile ? 2 : 4,
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        borderRadius: 2,
                        mb: 3,
                        backgroundColor: '#ffffff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}
                >
                    <Box sx={{
                        display: isMobile ? 'block' : 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        pb: 2,
                        borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
                    }}>
                        <Box>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 500,
                                    color: '#1a1a1a'
                                }}
                            >
                                Generate API Key
                            </Typography>
                            <RadioGroup
                                row
                                value={keyType}
                                onChange={(e) => setKeyType(e.target.value)}
                                sx={{ mt: 1 }}
                            >
                                <FormControlLabel value="read" control={<Radio />} label="Read" />
                                <FormControlLabel value="write" control={<Radio />} label="Write" />
                            </RadioGroup>
                        </Box>
                        <AddButton
                            startIcon={null}
                            label="Generate API"
                            onClick={handleGenerateAPI}
                            sx={{
                                fontSize: '14px',
                                px: 3,
                                mt: isMobile ? 2 : 0,
                                width: isMobile ? '100%' : '150px'
                            }}
                        />
                    </Box>

                    {generatedKey.length ? (
                        <Box>
                            <Box sx={{
                                display: isMobile ? 'block' : 'flex',
                                gap: 2,
                                alignItems: 'center'
                            }}>
                                <Typography sx={{
                                    fontFamily: 'monospace',
                                    p: 2,
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: 1,
                                    fontSize: '0.9rem',
                                    flex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    border: '1px solid rgba(0, 0, 0, 0.08)'
                                }}>
                                    {generatedKey}
                                    <IconButton
                                        color="secondary"
                                        onClick={() => handleCopy(generatedKey)}
                                        aria-label="copy"
                                        sx={{
                                            ml: 1,
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                            }
                                        }}
                                    >
                                        <CopyIcon fontSize="small" />
                                    </IconButton>
                                </Typography>
                                <AddButton
                                    startIcon={null}
                                    label="Save"
                                    onClick={handleSaveAPI}
                                    sx={{
                                        fontSize: '14px',
                                        textTransform: 'none',
                                        px: 3,
                                        mt: isMobile ? 2 : 0,
                                        width: isMobile ? '100%' : '100px'
                                    }}
                                />
                            </Box>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    color: '#ACACAC',
                                    fontSize: 14,

                                }}
                            >
                                Note : Api Key should be saved before use
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    color: '#1a1a1a'
                                }}
                            >
                                No API Key Generated
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>Your API Keys</Typography>
                    <Box sx={{
                        display: isMobile ? 'block' : 'flex',
                        gap: 2,
                        width: '100%',
                        maxWidth: '100%',
                    }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                                Read Keys
                            </Typography>
                            <Box sx={{ maxHeight: '240px', overflowY: 'auto', }}>
                                {apiKeys.read?.length > 0 ? (
                                    apiKeys.read.map((apiKey) => (
                                        <ApiKeyBox
                                            key={apiKey}
                                            apiKey={apiKey}
                                            onCopy={handleCopy}
                                            onDelete={handleDeleteAPI}
                                        />
                                    ))
                                ) : (
                                    <Typography variant="body2" sx={{ color: '#1a1a1a' }}>
                                        No read API keys found
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                                Write Keys
                            </Typography>
                            <Box sx={{ overflowY: 'auto', maxHeight: '240px' }}>
                                {apiKeys.write?.length > 0 ? (
                                    apiKeys.write.map((apiKey) => (
                                        <ApiKeyBox
                                            key={apiKey}
                                            apiKey={apiKey}
                                            onCopy={handleCopy}
                                            onDelete={handleDeleteAPI}
                                        />
                                    ))
                                ) : (
                                    <Typography variant="body2" sx={{ color: '#1a1a1a' }}>
                                        No write API keys found
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={() => setOpenSnackbar(false)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => setOpenSnackbar(false)}
                        severity={snackbarSeverity}
                        sx={{ width: '100%' }}
                    >{snackbarMessage}</Alert>

                </Snackbar>
                {loading && <LoadingOverlay />}
            </ModalBox>
        </Modal>
    );
};

ManageAPIKeysModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    selectedType: PropTypes.any,
    onSuccess: PropTypes.func
};


export default ManageAPIKeysModal;